import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Envelope, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * Admin tile — Invite a food truck owner.
 *
 * Pre-creates an owner account with no password. Sends them an email
 * with subject "You've been added to TruckTrackr" and a one-click link
 * to /onboarding/{token} where they choose their own password and
 * finish their profile. Resending to the same email is idempotent —
 * it rotates the token and re-sends. Active accounts (with a real
 * password) return 409 so we don't accidentally clobber them.
 */
export default function InviteOwnerCard({ onSent }) {
  const [email, setEmail] = useState("");
  const [truckName, setTruckName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !truckName.trim()) {
      toast.error("Email and truck name are both required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/users/invite", {
        email: email.trim().toLowerCase(),
        truck_name: truckName.trim(),
      });
      const previewN = data?.schedule_preview_count || 0;
      toast.success(
        previewN > 0
          ? `Invite sent to ${email.trim()} — email includes ${previewN} pre-built event${previewN === 1 ? "" : "s"}`
          : `Invite sent to ${email.trim()}`,
      );
      setEmail("");
      setTruckName("");
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send invite");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5"
      data-testid="admin-invite-owner-card"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-primary/15 grid place-items-center flex-none">
          <Envelope weight="duotone" className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
            Invite owner
          </div>
          <h3 className="font-display font-semibold text-lg sm:text-xl mt-0.5 leading-tight">
            Add a truck before they sign up
          </h3>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            We'll email them a one-click link to choose their password and
            finish their profile. No standard welcome email is sent — they get
            a tailored "You've been added to TruckTrackr" message instead.
            <br />
            <span className="text-foreground/80">
              Tip: pre-build their schedule first (or after — submit the same email + truck name to resend),
              and the email will include a "your week is already loaded" preview.
            </span>
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <Label htmlFor="invite-email">Owner email</Label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="hello@trucktrackr.com"
            autoComplete="off"
            required
            data-testid="admin-invite-email-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="invite-truck">Truck name</Label>
          <Input
            id="invite-truck"
            value={truckName}
            onChange={(e) => setTruckName(e.target.value)}
            placeholder="Smoke Test Tacos"
            required
            maxLength={120}
            data-testid="admin-invite-truck-input"
          />
        </div>
        <Button
          type="submit"
          disabled={submitting}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-10 px-5 gap-2"
          data-testid="admin-invite-submit-btn"
        >
          <PaperPlaneTilt weight="bold" className="h-4 w-4" />
          {submitting ? "Sending…" : "Send invite"}
        </Button>
      </form>
    </section>
  );
}
